import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getTravelPlans } from '../services/api';
import { format } from 'date-fns';
import Weather from '../components/Weather';
import { getCurrentWeather, getWeatherForecast } from '../services/weatherService';

function Calendar() {
  const [events, setEvents] = useState([]);
  const [weatherData, setWeatherData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (token) {
      fetchEvents();
      fetchWeatherData();
    }
  }, [token]);

  const fetchWeatherData = async () => {
    try {
      setIsLoading(true);
      const [currentData, forecast] = await Promise.all([
        getCurrentWeather('Tokyo'),
        getWeatherForecast('Tokyo')
      ]);
      
      if (currentData && currentData.current) {
        setWeatherData(currentData);
      }
      
      if (forecast && forecast.forecast) {
        setForecastData(forecast);
      }
    } catch (error) {
      console.error('Error fetching weather data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await getTravelPlans();
      
      const formattedEvents = response.data.map(plan => ({
        id: plan._id,
        title: plan.title,
        start: plan.startDate,
        end: plan.endDate,
        extendedProps: {
          destination: plan.destination,
          description: plan.description
        }
      }));
      
      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleDateClick = (arg) => {
    // Handle date click if needed
  };

  const handleEventClick = (arg) => {
    // Handle event click if needed
  };

  const getWeatherIcon = (condition) => {
    // Map weather conditions to icon URLs
    const iconMap = {
      'Clear': 'https://cdn-icons-png.flaticon.com/512/869/869869.png',
      'Cloudy': 'https://cdn-icons-png.flaticon.com/512/414/414927.png',
      'Rain': 'https://cdn-icons-png.flaticon.com/512/414/414974.png',
      'Snow': 'https://cdn-icons-png.flaticon.com/512/414/414975.png',
      'Thunderstorm': 'https://cdn-icons-png.flaticon.com/512/414/414976.png',
      'default': 'https://cdn-icons-png.flaticon.com/512/414/414927.png'
    };

    // Find matching condition
    const matchingCondition = Object.keys(iconMap).find(key => 
      condition.toLowerCase().includes(key.toLowerCase())
    );

    return iconMap[matchingCondition || 'default'];
  };

  const renderDayCell = (arg) => {
    const today = new Date();
    const cellDate = arg.date;
    const isToday = cellDate.toDateString() === today.toDateString();
    const isWithinForecast = forecastData && cellDate >= today && cellDate <= new Date(today.getTime() + 10 * 24 * 60 * 60 * 1000);
    
    let weatherIcon = null;
    let temperature = null;
    let condition = null;

    if (isToday && weatherData) {
      weatherIcon = getWeatherIcon(weatherData.current.weather_descriptions[0]);
      temperature = weatherData.current.temperature;
      condition = weatherData.current.weather_descriptions[0];
    } else if (isWithinForecast && forecastData) {
      const forecastDay = forecastData.forecast.forecastday.find(
        day => new Date(day.date).toDateString() === cellDate.toDateString()
      );
      
      if (forecastDay) {
        weatherIcon = getWeatherIcon(forecastDay.day.condition.text);
        temperature = forecastDay.day.avgtemp_c;
        condition = forecastDay.day.condition.text;
      }
    }

    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          background: weatherIcon ? `url(${weatherIcon})` : 'none',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '100px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.35)',
            zIndex: 1,
          },
        }}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            zIndex: 2, 
            p: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {arg.dayNumberText}
          </Typography>
          {weatherIcon && (
            <Box sx={{ mt: 'auto', textAlign: 'center' }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'primary.main', 
                  display: 'block',
                  fontWeight: 'bold',
                  fontSize: '1rem'
                }}
              >
                {temperature}°C
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary', 
                  display: 'block',
                  fontSize: '0.8rem'
                }}
              >
                {condition}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Travel Calendar</Typography>
      </Box>
      <Paper sx={{ p: 2 }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          events={events}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          dayCellContent={renderDayCell}
          height="auto"
          // Add accessibility enhancements
          focusOnMount={false}
        />
      </Paper>
    </Box>
  );
}

export default Calendar;